import { workerTypes } from '../constants'

export const contractorRateFieldKeys = [
    'weekdayMorningsRate',
    'weekdayNightsRate',
    'fridayNightRate',
    'saturdayDayRate',
    'saturdayNightRate',
    'sundayDayRate',
    'sundayNightRate',
]

export const contractorRateTypeLabels = {
    'Traffic Controller': 'Traffic Controller',
    'General Labourer': 'General Labour',
    'Skilled Labourer': 'Skilled Labour',
    'Skilled Worker A': 'Skilled Worker A',
    'Skilled Worker B': 'Skilled Worker B',
}

export const employeeRateFieldKeys = [
    'weekdayBaseRate',
    'weekdayOvertime0To2Rate',
    'weekdayOvertime2PlusRate',
    'saturdayFirst2Rate',
    'saturday2PlusRate',
    'sundayAllDayRate',
    'mealAllowance',
    'travelAllowance',
]
export const defaultContractorRateSchedulesByWorkerType = {
    'Traffic Controller': {
        weekdayMorningsRate: '30.00',
        weekdayNightsRate: '38.00',
        fridayNightRate: '38.00',
        saturdayDayRate: '38.00',
        saturdayNightRate: '50.00',
        sundayDayRate: '50.00',
        sundayNightRate: '50.00',
    },
    'General Labourer': {
        weekdayMorningsRate: '35.00',
        weekdayNightsRate: '40.00',
        fridayNightRate: '40.00',
        saturdayDayRate: '40.00',
        saturdayNightRate: '50.00',
        sundayDayRate: '50.00',
        sundayNightRate: '50.00',
    },
    'Skilled Labourer': {
        weekdayMorningsRate: '42.50',
        weekdayNightsRate: '48.00',
        fridayNightRate: '48.00',
        saturdayDayRate: '48.00',
        saturdayNightRate: '55.00',
        sundayDayRate: '55.00',
        sundayNightRate: '55.00',
    },
    'Skilled Worker A': {
        weekdayMorningsRate: '42.50',
        weekdayNightsRate: '48.00',
        fridayNightRate: '48.00',
        saturdayDayRate: '48.00',
        saturdayNightRate: '55.00',
        sundayDayRate: '55.00',
        sundayNightRate: '55.00',
    },
    'Skilled Worker B': {
        weekdayMorningsRate: '42.50',
        weekdayNightsRate: '48.00',
        fridayNightRate: '48.00',
        saturdayDayRate: '48.00',
        saturdayNightRate: '55.00',
        sundayDayRate: '55.00',
        sundayNightRate: '55.00',
    },
}

export const createEmptyContractorRates = () => Object.fromEntries(
    contractorRateFieldKeys.map(field => [
        field,
        Object.fromEntries(workerTypes.map(type => [type, ''])),
    ])
)

const normalizeContractorRateRow = (row) => {
    const source = row && typeof row === 'object' ? row : {}
    return Object.fromEntries(workerTypes.map(type => [type, source[type] ?? '']))
}

export const normalizeContractorRates = (rates) => {
    const source = rates && typeof rates === 'object' ? rates : {}
    return Object.fromEntries(
        contractorRateFieldKeys.map(field => [field, normalizeContractorRateRow(source[field])])
    )
}

export const hasAnyContractorRateValue = (rates) => contractorRateFieldKeys.some(field => (
    workerTypes.some(type => String(rates?.[field]?.[type] ?? '').trim() !== '')
))

export const getDefaultContractorRatesForWorkerType = (workerType) => {
    const defaultSchedule = defaultContractorRateSchedulesByWorkerType[workerType]
    const rates = createEmptyContractorRates()

    if (!defaultSchedule) return rates

    contractorRateFieldKeys.forEach(field => {
        rates[field][workerType] = defaultSchedule[field] ?? ''
    })

    return rates
}

export const applyDefaultContractorRates = (contractorRates, workerTypesValue = [], userType = '') => {
    const normalizedRates = normalizeContractorRates(contractorRates)
    const primaryType = getPrimaryWorkerType(workerTypesValue)

    if (userType !== 'Contractor' || hasAnyContractorRateValue(normalizedRates) || !primaryType) {
        return normalizedRates
    }

    return getDefaultContractorRatesForWorkerType(primaryType)
}

const createEmptyEmployeeRateSchedule = () => Object.fromEntries(employeeRateFieldKeys.map(field => [field, '']))

export const createEmptyEmployeeRates = () => ({
    day: createEmptyEmployeeRateSchedule(),
    night: createEmptyEmployeeRateSchedule(),
})

const normalizeEmployeeRateSchedule = (schedule) => {
    const source = schedule && typeof schedule === 'object' ? schedule : {}
    return Object.fromEntries(employeeRateFieldKeys.map(field => [field, source[field] ?? '']))
}

export const normalizeEmployeeRates = (rates) => {
    const source = rates && typeof rates === 'object' ? rates : {}
    return {
        day: normalizeEmployeeRateSchedule(source.day),
        night: normalizeEmployeeRateSchedule(source.night),
    }
}

export const inferWorkerUserType = (worker = {}) => {
    if (worker.userType) return worker.userType
    const workerTypesValue = Array.isArray(worker.workerType) ? worker.workerType : []
    return workerTypesValue.length > 0 ? 'Contractor' : ''
}

export const buildLegacyContractorRates = (worker = {}) => {
    const rates = createEmptyContractorRates()
    const types = Array.isArray(worker.workerType) ? worker.workerType : []

    types.forEach(type => {
        if (!workerTypes.includes(type)) return
        contractorRateFieldKeys.forEach(field => {
            rates[field][type] = worker[field] ?? ''
        })
    })

    return rates
}

export const getWorkerRateStructures = (worker = {}) => {
    const contractorRates = worker.contractorRates
        ? normalizeContractorRates(worker.contractorRates)
        : buildLegacyContractorRates(worker)

    const employeeRates = worker.employeeRates
        ? normalizeEmployeeRates(worker.employeeRates)
        : createEmptyEmployeeRates()

    return { contractorRates, employeeRates }
}

export const getPrimaryWorkerType = (workerTypesValue = []) => (
    Array.isArray(workerTypesValue) && workerTypesValue.length > 0 ? workerTypesValue[0] : ''
)

export const deriveLegacyRatesFromContractorRates = (contractorRates, workerTypesValue) => {
    const normalizedRates = normalizeContractorRates(contractorRates)
    const primaryType = getPrimaryWorkerType(workerTypesValue)

    return Object.fromEntries(
        contractorRateFieldKeys.map(field => [field, primaryType ? normalizedRates[field][primaryType] ?? '' : ''])
    )
}
